import {
  IEnvironmentVariables,
  IFetchUserInfo,
  IMembershipData,
  IUserInfo,
} from '../../../interfaces';
import { getEnv } from '../../environment';
import request from '../../services/request';
import durationUserInfo from '../../utils/durationUserInfo';
import { isPublicPage } from '../../utils/publicPage';
import { getFromStorage, removeFromStorage, setToStorage } from '../../utils/storage';
import userInfoKey from '../../utils/userInfoKey';
import { defaultUserInfo } from '../defaultContext';

const membershipStorageKey = `${userInfoKey}-membership`;

const fetchUserInfo = async ({ env }: IFetchUserInfo): Promise<IUserInfo> => {
  const response = await request<IUserInfo>({
    path: env.REACT_APP_USER_INFO_URL,
  });

  if (response && typeof response === 'object' && 'user' in response) {
    return response as IUserInfo;
  }

  return defaultUserInfo;
};

const fetchMembershipData = async ({
  env,
}: {
  env: IEnvironmentVariables;
}): Promise<IMembershipData | null> => {
  const cached = getMembershipFromStorage();

  try {
    const response = await request<IMembershipData>({
      path: env.REACT_APP_MEMBERSHIP_INFO_URL,
    });

    if (response && typeof response === 'object') {
      setToStorage({
        key: membershipStorageKey,
        value: response,
        durationInMs: durationUserInfo,
      });
      return response;
    }

    removeFromStorage({ key: membershipStorageKey });
    return null;
  } catch (err: unknown) {
    const error = err as Error;
    if (error.message === 'Unauthorized' || error.message === 'Forbidden') {
      removeFromStorage({ key: membershipStorageKey });
      return null;
    }

    return cached;
  }
};

const invalidateUserCache = () => {
  removeFromStorage({ key: userInfoKey });
  removeFromStorage({ key: membershipStorageKey });
};

const getMembershipFromStorage = (): IMembershipData | null =>
  (getFromStorage({ key: membershipStorageKey }) as IMembershipData | undefined) ?? null;

const getMembershipData = async ({
  env,
}: {
  env: IEnvironmentVariables;
}): Promise<IMembershipData | null> => {
  const cached = getMembershipFromStorage();
  if (cached || isPublicPage) return cached;
  return fetchMembershipData({ env });
};

const getUserFromStorage = () => getFromStorage({ key: userInfoKey, defaultItem: defaultUserInfo });

const getUserInfo = async (bypassPublicPageCheck = false): Promise<IUserInfo> => {
  // check if on public page and prevent user info fetch
  if (!bypassPublicPageCheck && isPublicPage) return Promise.resolve(defaultUserInfo);

  const userFromSTG = getFromStorage({ key: userInfoKey });
  if (userFromSTG) {
    return userFromSTG;
  }
  removeFromStorage({ key: userInfoKey });
  try {
    const requestUserInfo = await fetchUserInfo({ env: getEnv() });

    setToStorage({
      key: userInfoKey,
      value: requestUserInfo,
      durationInMs: durationUserInfo,
    });
    return requestUserInfo;
  } catch (err: unknown) {
    const error = err as Error;
    if (error.message === 'Unauthorized' || error.message === 'Forbidden') {
      removeFromStorage({ key: userInfoKey });
      removeFromStorage({ key: membershipStorageKey });
    } else {
      console.log(err, error.message);
    }
  }
  return Promise.resolve(defaultUserInfo);
};

export {
  fetchMembershipData,
  fetchUserInfo,
  getMembershipData,
  getMembershipFromStorage,
  invalidateUserCache,
  getUserFromStorage,
  getUserInfo,
};
